import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {change, Field, reduxForm} from 'redux-form';
import {connect} from "react-redux";
import {compose} from "recompose";
import {
    SelectInput,
} from 'admin-on-rest';
import {
    convertDate,
} from '../shared/functions';
import FormattedDateTextInput from './FormattedDateTextInput';
import {TIME_ZONE_OFFSETS} from '../shared/dateTimeLists';
import {filterStyles} from '../shared/styles-filter';

/**
 * A form containing a SelectInput component with Mode choices
 *
 */
class SalesComparisonReportsFilter extends Component {
    constructor(props) {
        super(props);
        this.state = {
            compareList: [],
            displayDateFilter: false,
            selectedStartDate: null,
            selectedStartDateObject: null,
            selectedEndDate: null,
            selectedEndDateObject: null,
            selectedOffset: (props.selectedOffset) ? props.selectedOffset : null,
            selectedCompareDates: null
        };
        this.handleStartDateSelect = this.handleStartDateSelect.bind(this);
        this.handleEndDateSelect = this.handleEndDateSelect.bind(this);
        this.handleOffsetSelect = this.handleOffsetSelect.bind(this);
        this.handleCompareDatesSelect = this.handleCompareDatesSelect.bind(this);
        this.generateCompareYears = this.generateCompareYears.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.selectedOffset !== this.props.selectedOffset) {
            this.setState({
                selectedOffset: nextProps.selectedOffset
            });
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const offset = this.props.selectedOffset;
        if (offset && offset !== prevProps.selectedOffset) {
            this.props.dispatch(change('filterForm', 'offset', offset));
            this.props.onSelectOffset('offset', offset);
            this.generateCompareYears();
        }
    }

    /* Selection Processing ------------------------------ */

    /**
     * Handle selection event for Start Date
     *
     * @param event
     * @param index
     * @param value
     */
    handleStartDateSelect(event, index, value) {
        if (index && index !== value) {
            // Convert date selection from YYYY-MM-DDT00:00:00.000Z to yyyy-mm-dd
            const dateStr = convertDate(index, 'query', false, true);
            this.setState({
                    selectedStartDate: dateStr,
                    selectedStartDateObject: index,
                    selectedEndDate: null,
                },
                () => {
                    this.props.dispatch(change('filterForm', 'start_date', dateStr));
                    this.props.onSelectStartDate('start_date', dateStr);
                    this.generateCompareYears();
                }
            );
        }
    }

    /**
     * Handle selection event for End Date
     *
     * @param event
     * @param index
     * @param value
     */
    handleEndDateSelect(event, index, value) {
        if (index && index !== value) {
            // Convert date selection from YYYY-MM-DDT00:00:00.000Z to yyyy-mm-dd
            const dateStr = convertDate(index, 'query', false, true);
            this.setState({
                    selectedEndDate: dateStr,
                    selectedEndDateObject: index
                },
                () => {
                    this.props.dispatch(change('filterForm', 'end_date', dateStr));
                    this.props.onSelectStartDate('end_date', dateStr);
                    this.generateCompareYears();
                }
            );
        }
    }

    /**
     * Handle selection event for Time Zone Offset
     *
     * @param event
     * @param index
     * @param value
     */
    handleOffsetSelect(event, index, value) {
        if (index && index !== value) {
            this.setState({
                    selectedOffset: index
                },
                () => {
                    this.props.dispatch(change('filterForm', 'offset', index));
                    this.props.onSelectOffset('offset', index);
                    this.generateCompareYears();
                });
        }
    }

    /**
     * Handle selection event for Compare Dates
     *
     * @param event
     * @param index
     * @param value
     */
    handleCompareDatesSelect(event, index, value) {
        if (index && index !== value) {
            // Send selected compare dates (format: 2018-02-01_2018-03-07)
            this.setState({
                    selectedCompareDates: index
                },
                () => {
                    this.props.dispatch(change('filterForm', 'compare_dates', index));
                    this.props.onSelectCompareDates('compare_dates', index);
                }
            );
        }
    }

    /**
     * Create a list of years backward from selected date range
     */
    generateCompareYears() {
        // Retrieve selected Start and End Dates, Offset and Earliest Year
        const selectedStartDate = this.state.selectedStartDateObject;
        const selectedEndDate = this.state.selectedEndDateObject;
        const offset = this.state.selectedOffset;

        if (selectedStartDate && selectedEndDate && offset) {
            const dateObj = new Date(selectedStartDate);

            // Set loop range (maximum 10 years)
            let lastYear = dateObj.getFullYear();
            let yearTotal = (lastYear - this.props.minCompareYear);
            if (yearTotal > 10) {
                yearTotal = 10;
            }
            let yearsList = [];

            // Assemble date ranges
            for (let i = 1; i <= yearTotal; i++) {

                // Create a date backward from start/end dates
                let startDate = new Date(selectedStartDate);
                let endDate = new Date(selectedEndDate);
                startDate.setMonth(startDate.getMonth() - (12 * i));
                endDate.setMonth(endDate.getMonth() - (12 * i));

                // Create a date range object to store
                let sId = convertDate(startDate, 'query', false, true);
                let eId = convertDate(endDate, 'query', false, true);
                let sName = convertDate(startDate, 'display', false, true);
                let eName = convertDate(endDate, 'display', false, true);
                let yearsBack = (i === 1) ? '1 Year Back: ' : i + ' Years Back: ';

                const compareObj = {
                    id: sId + '_' + eId,
                    name: yearsBack + sName + ' to ' + eName
                };
                yearsList.push(compareObj);
            }

            // Update the Select options
            this.setState({
                compareList: yearsList
            });
        }
    }

    render() {
        const {
            selectedOffset
        } = this.props;

        const {
            compareList,
            selectedStartDateObject,
            selectedEndDateObject
        } = this.state;

        const styles = {
            compareSelectInput: {width: 'auto', minWidth: 270, marginTop: 0},
        };

        return (
            <form>
                <div className="list-filter">
                    <FormattedDateTextInput
                        name="start_date"
                        source="start_date"
                        label="Select a Start Date:"
                        onChangeFunc={this.handleStartDateSelect}
                        maxDate={new Date()}
                    />
                    <FormattedDateTextInput
                        name="end_date"
                        source="end_date"
                        label="Select an End Date:"
                        onChangeFunc={this.handleEndDateSelect}
                        minDate={selectedStartDateObject}
                        maxDate={new Date()}
                        disabled={!selectedStartDateObject}
                    />
                    <Field name="offset"
                           component={SelectInput}
                           label="Select a Time Zone Offset"
                           choices={TIME_ZONE_OFFSETS}
                           onChange={this.handleOffsetSelect}
                           elStyle={filterStyles.selectInput}
                           options={{
                               value: selectedOffset
                           }}
                    />
                    <hr style={filterStyles.break}/>
                    <Field name="compare_dates"
                           component={SelectInput}
                           label="Select a set of Years to Compare"
                           choices={compareList}
                           onChange={this.handleCompareDatesSelect}
                           elStyle={styles.compareSelectInput}
                           options={{
                               disabled: !selectedStartDateObject || !selectedEndDateObject || !selectedOffset
                           }}
                    />
                </div>
            </form>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    let filterForm = state.form['filterForm'];
    let timeZoneForm = state.form['timeZoneForm'];
    const defaultOffsetValue = (timeZoneForm && timeZoneForm.values && timeZoneForm.values['timeZoneDefault']) ? timeZoneForm.values['timeZoneDefault'] : null;
    if (filterForm && filterForm.values) {
        const compareDatesValue = (filterForm.values['compare_dates']) ? filterForm.values['compare_dates'].toString() : null;
        const startDateValue = (filterForm.values['start_date']) ? filterForm.values['start_date'].toString() : null;
        const endDateValue = (filterForm.values['end_date']) ? filterForm.values['end_date'].toString() : null;
        const offsetValue = (filterForm.values['offset']) ? filterForm.values['offset'].toString() : defaultOffsetValue;
        return {
            selectedStartDate: startDateValue,
            selectedEndDate: endDateValue,
            selectedOffset: offsetValue,
            selectedCompareDates: compareDatesValue
        }
    }
    return {
        selectedOffset: defaultOffsetValue
    }
};

SalesComparisonReportsFilter.propTypes = {
    addField: PropTypes.bool,
    basePath: PropTypes.string,
    minCompareYear: PropTypes.number,
    onSelectStartDate: PropTypes.func,
    onSelectEndDate: PropTypes.func,
    onSelectOffset: PropTypes.func,
    onSelectCompareDates: PropTypes.func,
    submitOnEnter: PropTypes.bool,
};

SalesComparisonReportsFilter.defaultProps = {
    addField: true,
    minCompareYear: 2010,
    submitOnEnter: false,
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'filterForm',
        enableReinitialize: true,
    })
);

export default enhance(SalesComparisonReportsFilter);



// WEBPACK FOOTER //
// ./src/Fuse/components/SalesComparisonReportsFilter.js